import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  ChevronLeft, ChevronRight, Plus, Pencil, Trash2, CheckCircle2, Circle,
  Repeat, ListChecks, Search, X, Home,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const pad = (n) => String(n).padStart(2, '0');
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v) || 0);
const fmtDate = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '-');

const CAT_COLORS = {
  Luz: 'bg-yellow-100 text-yellow-800', 'Água': 'bg-sky-100 text-sky-800', Internet: 'bg-indigo-100 text-indigo-800',
  'Gás': 'bg-orange-100 text-orange-800', Aluguel: 'bg-rose-100 text-rose-800', Material: 'bg-amber-100 text-amber-800',
  'Manutenção': 'bg-slate-200 text-slate-700', Limpeza: 'bg-teal-100 text-teal-800', Mercado: 'bg-green-100 text-green-800',
  Impostos: 'bg-red-100 text-red-800', Outros: 'bg-slate-100 text-slate-600',
};

const emptyForm = { description: '', amount: '', date: '', category: 'Outros', notes: '', paid: false };
const emptyRecForm = { description: '', default_amount: '', due_day: '1', category: 'Outros', notes: '' };

const ExpenseForm = ({ form, setForm, categories, onSave, onCancel, saving, isEdit }) => (
  <div className="border border-emerald-300 bg-emerald-50/50 rounded-xl p-4 mb-4 space-y-3" data-testid="expense-form">
    <p className="font-semibold text-sm text-slate-800">{isEdit ? 'Editar Despesa' : 'Nova Despesa'}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium text-slate-600">Descrição *</label>
        <Input data-testid="expense-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Conta de luz" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Valor ($) *</label>
        <Input data-testid="expense-amount-input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Data *</label>
        <Input data-testid="expense-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Categoria</label>
        <select data-testid="expense-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white h-9">
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs font-medium text-slate-600">Observações</label>
        <Input data-testid="expense-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opcional" />
      </div>
    </div>
    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
      <input data-testid="expense-paid-checkbox" type="checkbox" checked={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
      Já foi pago
    </label>
    <div className="flex gap-2">
      <Button data-testid="expense-save-btn" size="sm" onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? 'Salvando...' : 'Salvar'}</Button>
      <Button data-testid="expense-cancel-btn" size="sm" variant="outline" onClick={onCancel}>Cancelar</Button>
    </div>
  </div>
);

const RecurringForm = ({ form, setForm, categories, onSave, onCancel, saving, isEdit }) => (
  <div className="border border-blue-300 bg-blue-50/50 rounded-xl p-4 mb-4 space-y-3" data-testid="recurring-form">
    <p className="font-semibold text-sm text-slate-800">{isEdit ? 'Editar Despesa Fixa' : 'Nova Despesa Fixa (mensal)'}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium text-slate-600">Descrição *</label>
        <Input data-testid="recurring-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Internet" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Valor padrão ($)</label>
        <Input data-testid="recurring-amount-input" type="number" step="0.01" value={form.default_amount} onChange={(e) => setForm({ ...form, default_amount: e.target.value })} placeholder="0.00" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Dia do vencimento</label>
        <Input data-testid="recurring-dueday-input" type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Categoria</label>
        <select data-testid="recurring-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white h-9">
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs font-medium text-slate-600">Observações</label>
        <Input data-testid="recurring-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opcional" />
      </div>
    </div>
    <div className="flex gap-2">
      <Button data-testid="recurring-save-btn" size="sm" onClick={onSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
      <Button data-testid="recurring-cancel-btn" size="sm" variant="outline" onClick={onCancel}>Cancelar</Button>
    </div>
  </div>
);

const SavannahExpensesPanel = ({ language = 'pt-BR' }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [view, setView] = useState('expenses');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, paid: 0, pending: 0, count: 0, count_paid: 0 });
  const [recurring, setRecurring] = useState([]);
  const [categories, setCategories] = useState(['Outros']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showRecForm, setShowRecForm] = useState(false);
  const [recForm, setRecForm] = useState(emptyRecForm);
  const [editingRecId, setEditingRecId] = useState(null);

  const monthKey = `${year}-${pad(month + 1)}`;

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/house-expenses`, { params: { month: monthKey }, headers: authHeaders() });
      setItems(res.data.items || []);
      setSummary(res.data.summary || { total: 0, paid: 0, pending: 0, count: 0, count_paid: 0 });
    } catch (e) { console.error('Erro ao carregar despesas', e); }
    setLoading(false);
  }, [monthKey]);

  const loadRecurring = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/house-expenses/recurring`, { headers: authHeaders() });
      setRecurring(res.data || []);
    } catch (e) { console.error('Erro ao carregar fixas', e); }
  }, []);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);
  useEffect(() => {
    loadRecurring();
    axios.get(`${API}/admin/house-expenses/categories`, { headers: authHeaders() })
      .then((r) => setCategories(r.data || ['Outros'])).catch(() => {});
  }, [loadRecurring]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  const togglePaid = async (item) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, paid: !i.paid } : i)));
    try {
      await axios.put(`${API}/admin/house-expenses/${item.id}`, { paid: !item.paid }, { headers: authHeaders() });
      loadExpenses();
    } catch (e) { loadExpenses(); }
  };

  const saveExpense = async () => {
    if (!form.description.trim() || !form.date) return;
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editingId) await axios.put(`${API}/admin/house-expenses/${editingId}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/house-expenses`, payload, { headers: authHeaders() });
      setShowForm(false); setForm(emptyForm); setEditingId(null);
      loadExpenses();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const startEdit = (item) => {
    setForm({ description: item.description, amount: String(item.amount), date: item.date, category: item.category || 'Outros', notes: item.notes || '', paid: !!item.paid });
    setEditingId(item.id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteExpense = async (item) => {
    if (!window.confirm(`Apagar a despesa "${item.description}"?`)) return;
    await axios.delete(`${API}/admin/house-expenses/${item.id}`, { headers: authHeaders() });
    loadExpenses();
  };

  const saveRecurring = async () => {
    if (!recForm.description.trim()) return;
    setSaving(true);
    try {
      const payload = { ...recForm, default_amount: parseFloat(recForm.default_amount) || 0, due_day: parseInt(recForm.due_day, 10) || 1 };
      if (editingRecId) await axios.put(`${API}/admin/house-expenses/recurring/${editingRecId}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/house-expenses/recurring`, payload, { headers: authHeaders() });
      setShowRecForm(false); setRecForm(emptyRecForm); setEditingRecId(null);
      loadRecurring(); loadExpenses();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const toggleRecActive = async (rec) => {
    await axios.put(`${API}/admin/house-expenses/recurring/${rec.id}`, { active: !rec.active }, { headers: authHeaders() });
    loadRecurring();
  };

  const deleteRecurring = async (rec) => {
    if (!window.confirm(`Apagar a despesa fixa "${rec.description}"? Os lançamentos já criados serão mantidos.`)) return;
    await axios.delete(`${API}/admin/house-expenses/recurring/${rec.id}`, { headers: authHeaders() });
    loadRecurring();
  };

  const q = search.toLowerCase();
  const filtered = items.filter((i) => {
    if (statusFilter === 'paid' && !i.paid) return false;
    if (statusFilter === 'pending' && i.paid) return false;
    if (q && !`${i.description} ${i.category} ${i.notes || ''}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div data-testid="savannah-expenses-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Home className="h-5 w-5" /></div>
          <div>
            <h3 className="font-bold text-slate-800">Despesas — Casa de Savannah</h3>
            <p className="text-xs text-slate-500">Controle de despesas com check de pagamento</p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 self-start">
          <button data-testid="sav-view-expenses" onClick={() => setView('expenses')} className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${view === 'expenses' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
            <ListChecks className="h-3.5 w-3.5" /> Lançamentos
          </button>
          <button data-testid="sav-view-recurring" onClick={() => setView('recurring')} className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${view === 'recurring' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
            <Repeat className="h-3.5 w-3.5" /> Fixas Mensais ({recurring.length})
          </button>
        </div>
      </div>

      {view === 'expenses' && (
        <>
          <div className="flex items-center justify-between mb-3 bg-white border rounded-xl px-3 py-2">
            <Button data-testid="sav-prev-month" variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="font-semibold text-slate-800 text-sm" data-testid="sav-month-label">{MONTHS_PT[month]} {year}</span>
            <Button data-testid="sav-next-month" variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="h-4 w-4" /></Button>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
            <div className="rounded-xl border bg-white p-3" data-testid="sav-summary-total">
              <p className="text-[11px] text-slate-500 uppercase font-medium">Total do Mês</p>
              <p className="text-lg font-bold text-slate-800">{money(summary.total)}</p>
              <p className="text-[11px] text-slate-400">{summary.count} despesa{summary.count !== 1 ? 's' : ''}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="sav-summary-paid">
              <p className="text-[11px] text-emerald-600 uppercase font-medium">Pago</p>
              <p className="text-lg font-bold text-emerald-700">{money(summary.paid)}</p>
              <p className="text-[11px] text-emerald-500">{summary.count_paid} de {summary.count}</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="sav-summary-pending">
              <p className="text-[11px] text-amber-600 uppercase font-medium">Pendente</p>
              <p className="text-lg font-bold text-amber-700">{money(summary.pending)}</p>
              <p className="text-[11px] text-amber-500">{summary.count - summary.count_paid} restante{summary.count - summary.count_paid !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="sav-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar despesa..." className="pl-9" />
              {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><X className="h-4 w-4" /></button>}
            </div>
            <div className="flex gap-1">
              {[['all', 'Todas'], ['pending', 'Pendentes'], ['paid', 'Pagas']].map(([k, label]) => (
                <button key={k} data-testid={`sav-filter-${k}`} onClick={() => setStatusFilter(k)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${statusFilter === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                  {label}
                </button>
              ))}
            </div>
            <Button data-testid="sav-new-expense-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => { setForm({ ...emptyForm, date: `${monthKey}-${pad(Math.min(today.getDate(), 28))}` }); setEditingId(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Nova Despesa
            </Button>
          </div>

          {showForm && (
            <ExpenseForm form={form} setForm={setForm} categories={categories} onSave={saveExpense} saving={saving} isEdit={!!editingId}
              onCancel={() => { setShowForm(false); setForm(emptyForm); setEditingId(null); }} />
          )}

          {loading ? (
            <p className="text-center text-slate-400 py-8 text-sm">Carregando...</p>
          ) : (
            <div className="space-y-2">
              {filtered.map((item) => (
                <div key={item.id} data-testid={`sav-expense-row-${item.id}`}
                  className={`flex items-center gap-3 p-3 border rounded-xl bg-white transition-colors ${item.paid ? 'border-emerald-200 bg-emerald-50/40' : ''}`}>
                  <button data-testid={`sav-toggle-paid-${item.id}`} onClick={() => togglePaid(item)} className="shrink-0" aria-label={item.paid ? 'Marcar como pendente' : 'Marcar como pago'}>
                    {item.paid ? <CheckCircle2 className="h-6 w-6 text-emerald-500" /> : <Circle className="h-6 w-6 text-slate-300 hover:text-emerald-400" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium text-sm ${item.paid ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{item.description}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CAT_COLORS[item.category] || CAT_COLORS.Outros}`}>{item.category}</span>
                      {item.recurring_id && <Repeat className="h-3 w-3 text-blue-400" title="Despesa fixa" />}
                    </div>
                    <p className="text-xs text-slate-500">
                      {fmtDate(item.date)}
                      {item.paid && item.paid_date && ` • pago em ${fmtDate(item.paid_date.slice(0, 10))}`}
                      {item.notes && ` • ${item.notes}`}
                    </p>
                  </div>
                  <span className={`font-bold text-sm shrink-0 ${item.paid ? 'text-emerald-600' : 'text-slate-800'}`}>{money(item.amount)}</span>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`sav-edit-${item.id}`} onClick={() => startEdit(item)} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`sav-delete-${item.id}`} onClick={() => deleteExpense(item)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <p className="text-center text-slate-400 py-10 text-sm" data-testid="sav-empty-list">
                  {items.length === 0 ? 'Nenhuma despesa neste mês. Clique em "Nova Despesa" para começar.' : 'Nenhuma despesa encontrada com esse filtro.'}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {view === 'recurring' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-slate-500">Despesas fixas são lançadas automaticamente todo mês — você só confere o valor e marca como pago.</p>
            <Button data-testid="sav-new-recurring-btn" size="sm" onClick={() => { setRecForm(emptyRecForm); setEditingRecId(null); setShowRecForm(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Nova Fixa
            </Button>
          </div>

          {showRecForm && (
            <RecurringForm form={recForm} setForm={setRecForm} categories={categories} onSave={saveRecurring} saving={saving} isEdit={!!editingRecId}
              onCancel={() => { setShowRecForm(false); setRecForm(emptyRecForm); setEditingRecId(null); }} />
          )}

          <div className="space-y-2">
            {recurring.map((rec) => (
              <div key={rec.id} data-testid={`sav-recurring-row-${rec.id}`} className={`flex items-center gap-3 p-3 border rounded-xl bg-white ${!rec.active ? 'opacity-50' : ''}`}>
                <Repeat className="h-5 w-5 text-blue-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-800">{rec.description}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CAT_COLORS[rec.category] || CAT_COLORS.Outros}`}>{rec.category}</span>
                    {!rec.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500">pausada</span>}
                  </div>
                  <p className="text-xs text-slate-500">Vence dia {rec.due_day} • valor padrão {money(rec.default_amount)}{rec.notes && ` • ${rec.notes}`}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button data-testid={`sav-rec-toggle-${rec.id}`} size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => toggleRecActive(rec)}>
                    {rec.active ? 'Pausar' : 'Ativar'}
                  </Button>
                  <button data-testid={`sav-rec-edit-${rec.id}`} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                    onClick={() => { setRecForm({ description: rec.description, default_amount: String(rec.default_amount), due_day: String(rec.due_day), category: rec.category || 'Outros', notes: rec.notes || '' }); setEditingRecId(rec.id); setShowRecForm(true); }}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`sav-rec-delete-${rec.id}`} onClick={() => deleteRecurring(rec)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {recurring.length === 0 && !showRecForm && (
              <p className="text-center text-slate-400 py-10 text-sm" data-testid="sav-recurring-empty">
                Nenhuma despesa fixa cadastrada. Ex: Luz, Água, Internet...
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SavannahExpensesPanel;
